// REL_1.1.15E F-313 (S-1229) — Home-page rebuild to the operator's 6-section wireframe (US-primary).
// Replaces the old "choose your country" landing (hide-Canada, S-1228). Responsive / mobile-first,
// accessible (WCAG AA), English-only. Copy Business-verified accurate to SYSTEM_PRD, with two mandatory
// edits applied: (1) Trust "Zero Hallucinations" → "Answers Only From Your Documents" (defensible framing,
// operator-approved); (2) workflow step-4 "US/Canadian phone numbers" → "US" (hide-Canada). All primary
// CTAs cross-domain to the app phone-first signup (VITE_APP_BASE_URL/signup — Identity CORS marketingOrigin).
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck,
  Gauge,
  MessageSquareWarning,
  Upload,
  ClipboardCheck,
  QrCode,
  UserCheck,
  MessagesSquare,
  Tags,
  Megaphone,
  LayoutDashboard,
  Check,
  X,
} from "lucide-react";

const MARKETING_BASE = import.meta.env.VITE_MARKETING_BASE_URL || "https://www.deedo.ai";
const APP_BASE_URL: string = import.meta.env.VITE_APP_BASE_URL || "https://app.deedo.ai";
const SIGNUP_URL = `${APP_BASE_URL}/signup`;
const PROMO_DISMISS_KEY = "deedo-home-promo-dismissed";

// ── 1 · Sticky promo banner (dismissible) ──
function PromoBanner() {
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(PROMO_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  if (hidden) return null;
  const dismiss = () => {
    try {
      localStorage.setItem(PROMO_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setHidden(true);
  };
  return (
    // brand-primary (#0A2B6E) not brand-secondary (#00B4D8 cyan): white text needs AA contrast (F-275).
    <div
      role="status"
      className="sticky top-0 z-30 flex items-center justify-center gap-3 bg-[var(--brand-primary)] px-4 py-2 text-center text-sm font-medium text-white"
    >
      <span>
        🚀 <span className="font-semibold">Launch Special:</span> Claim your first listing absolutely FREE
        for the first 30 days. No credit card required upfront.
      </span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="shrink-0 rounded p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

// ── 2 · Hero ──
function Hero() {
  return (
    <section className="bg-gradient-to-br from-[var(--brand-primary)] via-[var(--brand-primary-light)] to-[var(--brand-primary)] text-white">
      <div className="mx-auto max-w-5xl px-4 py-20 text-center lg:px-8 lg:py-28">
        <h1 className="mb-5 text-4xl font-bold leading-tight md:text-6xl">
          Put an AI Concierge on Your Next Listing.
        </h1>
        <p className="mx-auto max-w-3xl text-lg leading-relaxed text-white/85 md:text-xl">
          Upload your property disclosures, generate a custom QR code, and capture verified leads at your
          open house. Deedo's AI answers buyer questions instantly using only your approved documents—freeing
          you to close deals, not chase tire-kickers.
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={SIGNUP_URL}
            className="w-full rounded-lg bg-white px-7 py-3 text-base font-semibold text-[var(--brand-primary)] shadow-sm transition-transform hover:scale-[1.02] sm:w-auto"
          >
            Claim Your Free Listing
          </a>
          <a
            href="#workflow"
            className="w-full rounded-lg border border-white/60 px-7 py-3 text-base font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto"
          >
            See How the AI Works
          </a>
        </div>
      </div>
    </section>
  );
}

// ── 3 · Trust & Safety ──
const TRUST_CARDS = [
  {
    Icon: ShieldCheck,
    title: "Answers Only From Your Documents",
    body: "The AI only answers from the PDFs, DOCXs, and text you upload—no made-up answers, no guessing.",
  },
  {
    Icon: Gauge,
    title: "AI Readiness Score",
    body: "An automated audit grades your listing's data from 0–100%. If the AI lacks enough information to be accurate, the listing doesn't go Active.",
  },
  {
    Icon: MessageSquareWarning,
    title: "Smart Escalation",
    body: "On a subjective question the AI gracefully defers and sends a crisp two-sentence summary straight to your phone.",
  },
];
function TrustSafety() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <h2 className="text-center text-3xl font-bold text-gray-900">Grounded in Facts. Built for Trust.</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {TRUST_CARDS.map(({ Icon, title, body }) => (
            <div key={title} className="rounded-xl border border-gray-200 bg-white p-7 shadow-sm">
              <Icon className="h-8 w-8 text-[var(--brand-secondary)]" aria-hidden="true" />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── 4 · Workflow ──
const WORKFLOW_STEPS = [
  {
    Icon: Upload,
    title: "Upload Your Property Docs",
    body: "PDF, DOCX, or text—disclosures, inspection reports, specs. This builds the property's knowledge base.",
  },
  {
    Icon: ClipboardCheck,
    title: "Pass the AI Readiness Check",
    body: "An automated 8-category audit scores the listing. Once the score crosses the threshold, the listing can go Active.",
  },
  {
    Icon: QrCode,
    title: "Deploy Your QR Code",
    body: "Print-ready templates for yard signs and flyers. Buyers scan for an anonymous preview and instant Q&A.",
  },
  {
    Icon: UserCheck,
    // COPY EDIT #2 (F-313 hide-Canada): "US/Canadian" → "US".
    title: "Capture Verified Leads",
    body: "Phone-first identity: buyers authenticate via SMS one-time code, so you capture real, verified US phone numbers.",
  },
];
function Workflow() {
  return (
    <section id="workflow" className="scroll-mt-16 bg-[var(--brand-neutral-light)] py-20">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <h2 className="text-center text-3xl font-bold text-gray-900">Open Houses, Redefined</h2>
        <ol className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {WORKFLOW_STEPS.map(({ Icon, title, body }, i) => (
            <li key={title} className="relative rounded-xl bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--brand-primary)] text-sm font-bold text-white">
                  {i + 1}
                </span>
                <Icon className="h-6 w-6 text-[var(--brand-secondary)]" aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">{body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ── 5 · Command Center (2×2) ──
const COMMAND_FEATURES = [
  {
    Icon: MessagesSquare,
    title: "Dedicated Chat Rooms",
    body: "A Q&A Room (buyer ↔ AI), Room A for your internal listing team, and Room B for private broker negotiations.",
  },
  {
    Icon: Tags,
    title: "Interest-Level Tagging",
    body: "Tag every buyer: Will Consider Offer · Very Interested · Considering · Browsing · Not Interested.",
  },
  {
    Icon: Megaphone,
    title: "Targeted Broadcasts",
    body: "Announce an offer deadline to just the buyers tagged “Very Interested”—no spraying your whole list.",
  },
  {
    Icon: LayoutDashboard,
    title: "Consumer Property Hub",
    body: "Buyers get a dashboard of saved favorites, active chats, and their interest levels across listings.",
  },
];
function CommandCenter() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <h2 className="text-center text-3xl font-bold text-gray-900">Total Control Over Your Pipeline</h2>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {COMMAND_FEATURES.map(({ Icon, title, body }) => (
            <div key={title} className="flex gap-4 rounded-xl border border-gray-200 p-6">
              <Icon className="h-7 w-7 shrink-0 text-[var(--brand-secondary)]" aria-hidden="true" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── 6 · Pricing & Risk Reversal ──
const PRICING_INCLUDES = [
  "1,000 AI factual Q&A credits every month",
  "Full access to Team Rooms, Broadcasts & Lead Capture",
  "Listing archiving included",
];
function PricingCard() {
  return (
    <section className="bg-[var(--brand-neutral-light)] py-20">
      <div className="mx-auto max-w-xl px-4 lg:px-8">
        <h2 className="text-center text-3xl font-bold text-gray-900">Predictable Pricing. Zero Friction.</h2>
        <div className="mt-10 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="bg-[var(--brand-primary)] px-8 py-8 text-center text-white">
            <p className="text-5xl font-bold">
              $40<span className="text-lg font-medium text-white/80">/month</span>
            </p>
            <p className="mt-1 text-sm text-white/80">per active property</p>
          </div>
          <div className="px-8 py-8">
            <ul className="space-y-3">
              {PRICING_INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand-success)]" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-lg bg-[var(--brand-secondary-light)] px-4 py-3 text-sm text-gray-800">
              <span className="font-semibold">The FirstPropertyFree Guarantee:</span> your first listing is
              100% free for 30 days—no credit card to activate. Walk away before day 30 and pay nothing.
            </div>
            <a
              href={SIGNUP_URL}
              className="mt-7 block w-full rounded-lg bg-[var(--brand-primary)] px-6 py-3 text-center text-base font-semibold text-white transition-opacity hover:opacity-90"
            >
              Start Your Phone-First Sign-Up
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>Deedo — Put an AI Concierge on Your Next Listing</title>
        <meta
          name="description"
          content="Deedo puts an AI concierge on your real-estate listing: upload your docs, generate a QR code, and capture verified leads. The AI answers buyer questions using only your approved documents."
        />
        <link rel="canonical" href={`${MARKETING_BASE}/`} />
        <meta property="og:title" content="Deedo — Put an AI Concierge on Your Next Listing" />
        <meta
          property="og:description"
          content="Upload your docs, deploy a QR code, capture verified leads. Deedo's AI answers buyers using only your approved documents."
        />
        <meta property="og:url" content={`${MARKETING_BASE}/`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <PromoBanner />
      <Hero />
      <TrustSafety />
      <Workflow />
      <CommandCenter />
      <PricingCard />
    </>
  );
}
